import { useState, FormEvent } from 'react';

export default function FinalCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    company: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      setError('Please fill in all required fields.');
      return;
    }
    if (formData.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }
    setError('');
    setLoading(true);

    const body = new URLSearchParams();
    Object.entries(formData).forEach(([key, value]) => body.append(key, value));

    try {
      await fetch('https://readdy.ai/api/form/d6ulaa6dp0k8rarn7mbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-slate-50 py-28 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-slate-200" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 border border-orange-300 mb-5">
            <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
            <span className="text-orange-700 text-sm font-bold">Limited Slots — Only 8 Free Assessment Spots Left This Month</span>
          </div>
          <h2 className="font-display text-5xl lg:text-6xl font-black text-slate-900 mb-4">
            Get Your Free<br />
            <span className="text-gradient-orange">Security Quote Today</span>
          </h2>
          <p className="text-slate-600 text-lg max-w-xl mx-auto">
            Our specialists visit your property, design a custom system, and deliver a{' '}
            <strong className="text-slate-900">transparent quote — no cost, no obligation.</strong>
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start max-w-6xl mx-auto">
          {/* Form */}
          <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-8">
            {submitted ? (
              <div className="text-center py-14">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-orange-100 border-2 border-orange-300 mx-auto mb-6">
                  <i className="ri-check-double-fill text-orange-600 text-4xl" />
                </div>
                <h3 className="font-display text-3xl font-black text-slate-900 mb-3">Request Received!</h3>
                <p className="text-slate-500 text-base">
                  A Secure2Ware specialist will contact you within{' '}
                  <strong className="text-orange-600">15 minutes</strong> during business hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                <div className="mb-7">
                  <h3 className="font-display text-2xl font-black text-slate-900 mb-1">
                    Get a Free Security System Quote
                  </h3>
                  <p className="text-slate-500 text-sm">No cost · No commitment · Same-day scheduling available</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-700 text-xs font-bold mb-2 uppercase tracking-wide">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 text-xs font-bold mb-2 uppercase tracking-wide">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company (optional)"
                      className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-700 text-xs font-bold mb-2 uppercase tracking-wide">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 text-xs font-bold mb-2 uppercase tracking-wide">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 000-0000"
                      className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-700 text-xs font-bold mb-2 uppercase tracking-wide">
                    Service Needed
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 text-sm focus:outline-none focus:border-orange-400 transition-all appearance-none cursor-pointer"
                  >
                    <option value="">Select a service...</option>
                    <option value="Security Camera Installation">Security Camera Installation</option>
                    <option value="Surveillance System (CCTV)">Surveillance System (CCTV)</option>
                    <option value="Alarm System">Alarm System</option>
                    <option value="Access Control Systems">Access Control Systems</option>
                    <option value="Smart Home Automation">Smart Home Automation</option>
                    <option value="Business Security System">Business Security System</option>
                    <option value="Full Security Package">Full Security Package</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 text-xs font-bold mb-2 uppercase tracking-wide">
                    Message
                    <span className="ml-2 text-slate-400 normal-case font-normal">({formData.message.length}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={3}
                    placeholder="Tell us about your property and security needs..."
                    className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 text-sm placeholder-slate-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all resize-none"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
                    <i className="ri-error-warning-fill text-red-500" />
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-4 rounded-xl bg-slate-900 text-white font-black text-base hover:bg-slate-800 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-camera-fill" />
                      Get My Free Security Quote
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-6 pt-1">
                  {[
                    { icon: 'ri-lock-fill', text: 'SSL Encrypted' },
                    { icon: 'ri-mail-forbid-fill', text: 'No Spam' },
                    { icon: 'ri-timer-flash-fill', text: '15-Min Response' },
                  ].map((item) => (
                    <div key={item.text} className="flex items-center gap-1.5 text-slate-400 text-xs">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className={`${item.icon} text-xs`} />
                      </div>
                      {item.text}
                    </div>
                  ))}
                </div>
              </form>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-4">
            {/* Phone CTA */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mb-4">Or Contact Us Directly</p>
              <a href="tel:+18005551234" className="flex items-center gap-4 mb-5 group cursor-pointer">
                <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-orange-100 border border-orange-200 group-hover:bg-orange-200 transition-colors flex-shrink-0">
                  <i className="ri-phone-fill text-orange-600 text-2xl" />
                </div>
                <div>
                  <p className="text-slate-900 font-black text-2xl group-hover:text-orange-600 transition-colors">
                    (800) 555-1234
                  </p>
                  <p className="text-slate-400 text-xs">Click to call · Available 24/7</p>
                </div>
              </a>
              <a href="mailto:info@secure2ware.com" className="flex items-center gap-3 mb-4 group cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 border border-slate-200 flex-shrink-0">
                  <i className="ri-mail-fill text-slate-500 text-lg" />
                </div>
                <span className="text-slate-600 text-sm group-hover:text-orange-600 transition-colors">
                  info@secure2ware.com
                </span>
              </a>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 border border-slate-200 flex-shrink-0">
                  <i className="ri-time-fill text-slate-500 text-lg" />
                </div>
                <div>
                  <p className="text-slate-700 text-sm">Mon–Sat: 7am–9pm</p>
                  <p className="text-orange-600 text-xs font-semibold">24/7 Emergency Support</p>
                </div>
              </div>
            </div>

            {/* Trust items */}
            {[
              { icon: 'ri-shield-check-fill', label: 'Free Security Assessment', sub: 'No obligation, no cost — ever' },
              { icon: 'ri-timer-flash-fill', label: 'Same-Day Service Available', sub: 'Most installs within 24 hours' },
              { icon: 'ri-tools-fill', label: '10-Year Warranty', sub: 'On all installation workmanship' },
              { icon: 'ri-award-fill', label: 'Licensed & Fully Insured', sub: 'Background-checked technicians' },
            ].map((item) => (
              <div key={item.label} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-100 border border-orange-200 flex-shrink-0">
                  <i className={`${item.icon} text-orange-600 text-lg`} />
                </div>
                <div>
                  <p className="text-slate-900 font-bold text-sm">{item.label}</p>
                  <p className="text-slate-500 text-xs">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
